import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { api, apiError } from "@/lib/api";
import { isNative, takePhoto } from "@/lib/native";
import { toast } from "sonner";
import { IdCard, Camera, Upload, X, ShieldCheck, ArrowRight } from "lucide-react";

/**
 * In-context "Quick verify" modal — slide-up bottom sheet on mobile,
 * centered modal on desktop. Reuses the existing
 * `POST /api/verification/upload` endpoint so verification submitted
 * here lands in the same admin queue as the full `/verify` page.
 *
 * On success the modal stays open showing a "we got it" celebration
 * for 1.5s, then calls `onSubmitted()` — the parent can either close
 * + reload the stream (which will then render the "we're reviewing"
 * gate variant), or route the user to their dashboard, whatever
 * makes sense.
 */
export default function QuickVerifyModal({ open, onClose, onSubmitted }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRef = useRef(null);

  // Cleanup preview blob URL on unmount / file change.
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Reset state every time we re-open.
  useEffect(() => {
    if (open) {
      setFile(null);
      setSuccess(false);
    }
  }, [open]);

  const pickFromDevice = () => inputRef.current?.click();

  const captureNative = async () => {
    try {
      const photo = await takePhoto();
      if (!photo) return;
      // takePhoto returns either a File-ish object or a base64 string;
      // normalize to a File.
      if (photo instanceof File) {
        setFile(photo);
      } else if (photo?.dataUrl) {
        const blob = await (await fetch(photo.dataUrl)).blob();
        setFile(new File([blob], `verify-${Date.now()}.jpg`, { type: "image/jpeg" }));
      }
    } catch (e) {
      toast.error("Camera unavailable — pick from your gallery instead.");
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!file || uploading) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/verification/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess(true);
      toast.success("Submitted — we're reviewing your ID");
      setTimeout(() => {
        onSubmitted?.();
      }, 1400);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
      data-testid="quick-verify-modal"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Sheet */}
      <div
        className="relative bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto shadow-2xl animate-in slide-in-from-bottom duration-200"
        role="dialog"
        aria-modal="true"
      >
        <div className="sticky top-0 bg-white border-b border-zinc-100 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-[#1D78AB] text-white grid place-items-center">
              <IdCard size={15} />
            </div>
            <span className="font-display font-black text-sm tracking-tight">
              Quick verify
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-full hover:bg-zinc-100 grid place-items-center text-zinc-500"
            aria-label="Close"
            data-testid="quick-verify-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 pb-7">
          {success ? (
            <div className="text-center py-6" data-testid="quick-verify-success">
              <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-500 text-white grid place-items-center mb-3 shadow-md">
                <ShieldCheck size={28} strokeWidth={2.4} />
              </div>
              <div className="font-display font-black text-xl tracking-tight">
                Got it — review started
              </div>
              <p className="text-sm text-zinc-600 mt-1.5">
                We'll ping you the moment you're approved. Usually under 24 hours.
              </p>
            </div>
          ) : (
            <>
              <h2 className="font-display font-black text-2xl tracking-tight leading-tight">
                Snap your ID. <br />
                Drop back into the show.
              </h2>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                We need one clear photo of your US government-issued ID. Most approvals come back in minutes — you'll be back in the live show in no time.
              </p>

              {preview ? (
                <div className="mt-4 relative rounded-2xl overflow-hidden border border-zinc-200 bg-zinc-100">
                  <img
                    src={preview}
                    alt="ID preview"
                    className="w-full h-48 object-contain bg-zinc-900/5"
                    data-testid="quick-verify-preview"
                  />
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="absolute top-2 right-2 rounded-full bg-black/70 text-white text-xs font-bold px-3 py-1"
                    data-testid="quick-verify-clear"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={isNative() ? captureNative : pickFromDevice}
                    className="rounded-2xl border-2 border-zinc-200 hover:border-[#1D78AB] hover:bg-[#1D78AB]/5 transition-colors py-6 px-3 flex flex-col items-center gap-2"
                    data-testid="quick-verify-camera-btn"
                  >
                    <Camera size={22} className="text-[#1D78AB]" />
                    <span className="text-xs font-bold">
                      {isNative() ? "Use camera" : "Take photo"}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={pickFromDevice}
                    className="rounded-2xl border-2 border-zinc-200 hover:border-[#1D78AB] hover:bg-[#1D78AB]/5 transition-colors py-6 px-3 flex flex-col items-center gap-2"
                    data-testid="quick-verify-upload-btn"
                  >
                    <Upload size={22} className="text-[#1D78AB]" />
                    <span className="text-xs font-bold">Upload photo</span>
                  </button>
                </div>
              )}
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setFile(f);
                }}
                className="hidden"
                data-testid="quick-verify-file-input"
              />

              <button
                type="button"
                disabled={!file || uploading}
                onClick={submit}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1D78AB] hover:bg-[#176089] disabled:opacity-50 disabled:cursor-not-allowed text-white font-black px-6 py-3 text-sm tracking-tight transition-colors"
                data-testid="quick-verify-submit"
              >
                {uploading ? "Uploading…" : "Submit for review"}
                {!uploading && <ArrowRight size={15} />}
              </button>

              <ul className="mt-4 space-y-1.5 text-[11px] text-zinc-500">
                <li className="flex items-start gap-2">
                  <span className="inline-block h-1 w-1 rounded-full bg-zinc-400 mt-1.5" />
                  US driver's license, state ID, or passport — all OK
                </li>
                <li className="flex items-start gap-2">
                  <span className="inline-block h-1 w-1 rounded-full bg-zinc-400 mt-1.5" />
                  Make sure all four corners are visible and text is sharp
                </li>
                <li className="flex items-start gap-2">
                  <span className="inline-block h-1 w-1 rounded-full bg-zinc-400 mt-1.5" />
                  We never share your ID. It's used once for verification then encrypted at rest.
                </li>
              </ul>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
